/**
 * DropdownMenu on Radix. DESIGN.md has no menu section; this follows its overlay rules:
 * a flat surface with a 1px divider border, 10px (md) radius and the pass shadow
 * (shadow-2, "Material Lifts": overlays cast shadow), 44px rows (the 44px Touch Rule),
 * a surface-selected tint on the highlighted row (Radix moves DOM focus to it, so the
 * `focus:` variant is the highlight), danger text for destructive rows. Radix owns arrow
 * keys, typeahead, Escape and focus return.
 */
import React, { forwardRef } from 'react';
import * as RadixMenu from '@radix-ui/react-dropdown-menu';
import { cva } from 'class-variance-authority';
import { cn } from './cn';
import { SurfaceRoot, useSurfaceClass } from './surface';

export function DropdownMenu({ surface, children, ...props }) {
  return (
    <RadixMenu.Root {...props}>
      <SurfaceRoot surface={surface}>{children}</SurfaceRoot>
    </RadixMenu.Root>
  );
}
export const DropdownMenuTrigger = RadixMenu.Trigger;

export const DropdownMenuContent = forwardRef(function DropdownMenuContent({ className, sideOffset = 4, ...props }, ref) {
  const surfaceClass = useSurfaceClass();
  return (
    <RadixMenu.Portal>
      <RadixMenu.Content
        ref={ref}
        sideOffset={sideOffset}
        className={cn(
          'tw-z-50 tw-w-56 tw-p-1 tw-rounded-md tw-border tw-border-divider tw-bg-surface tw-text-text tw-shadow-2',
          surfaceClass,
          className,
        )}
        {...props}
      />
    </RadixMenu.Portal>
  );
});

const item = cva(
  [
    'tw-flex tw-items-center tw-gap-2 tw-min-h-11 tw-px-3 tw-rounded-sm tw-text-sm tw-font-semibold',
    'tw-cursor-pointer tw-outline-none tw-transition-colors tw-duration-fast tw-ease-out',
    'focus:tw-bg-surface-selected aria-disabled:tw-opacity-45 aria-disabled:tw-cursor-not-allowed',
  ],
  {
    variants: { variant: { default: 'tw-text-text', danger: 'tw-text-danger' } },
    defaultVariants: { variant: 'default' },
  },
);

export const DropdownMenuItem = forwardRef(function DropdownMenuItem({ className, variant, ...props }, ref) {
  return <RadixMenu.Item ref={ref} className={cn(item({ variant }), className)} {...props} />;
});

export const DropdownMenuSeparator = forwardRef(function DropdownMenuSeparator({ className, ...props }, ref) {
  return <RadixMenu.Separator ref={ref} className={cn('tw-h-px tw-my-1 tw-bg-divider', className)} {...props} />;
});

export const DropdownMenuLabel = forwardRef(function DropdownMenuLabel({ className, ...props }, ref) {
  return <RadixMenu.Label ref={ref} className={cn('tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-text-muted', className)} {...props} />;
});
